import { ContentState, updateContentList } from "@platformx/authoring-state";
import {
  ShowToastError,
  ShowToastSuccess,
  capitalizeFirstLetter,
  frameDocumentType,
  isFixedContentType,
  isScheduledInFuture,
  validateSchedule,
} from "@platformx/utilities";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { ScheduleAction, ScheduleFamily } from "../../graphQL/mutations/contentScheduleMutations";
import contentScheduleApi from "../../services/contentSchedule/contentSchedule.api";
import contentTypeAPIs from "../../services/contentTypes/contentTypes.api";
import { getScheduleErrorInfo, getScheduleErrorText } from "../../utils/scheduleErrors";
import {
  SCHEDULE_ROW_FIELD,
  ScheduleKind,
  ScheduleRowPatch,
  applyScheduleRowPatch,
} from "../../utils/scheduleRowPatch";

export type { ScheduleKind };

/**
 * Re-reads the listing after a schedule change. The listing screen supplies this so the re-read uses
 * its own filters, search, sort and page size; `patch` is the new truth for the changed row.
 */
export type ScheduleListRefresh = (item: any, patch?: ScheduleRowPatch) => Promise<void>;

/**
 * Amend or cancel a pending schedule on a content row from the listing (KIWI-6576).
 */
const useContentSchedule = (
  filter = "ALL",
  isShared = false,
  refresh: ScheduleListRefresh | undefined = undefined,
) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { contentList, startIndex } = useSelector((state: ContentState) => state);
  const [loading, setLoading] = useState(false);

  const familyOf = (item: any): ScheduleFamily =>
    isFixedContentType(item?.tag_name) ? "fixed" : "schema";

  const buildVariables = (
    item: any,
    schemaType: string,
    family: ScheduleFamily,
    scheduleDateTime?: string,
  ) => {
    const when = scheduleDateTime ? { schedule_date_time: scheduleDateTime } : {};
    if (family === "fixed") {
      return {
        contentType: item.tag_name,
        input: { page: item.page || item?.Page, ...when },
      };
    }
    return {
      contentType: item.tag_name,
      schema_document_type: frameDocumentType(schemaType),
      input: { document_name: item.page, ...when },
    };
  };

  const refreshList = async (
    item: any,
    schemaType: string,
    family: ScheduleFamily,
    patch?: ScheduleRowPatch,
  ) => {
    if (refresh) {
      await refresh(item, patch);
      return;
    }
    const response = await contentTypeAPIs.fetchSearchContent(
      item.tag_name,
      location,
      filter,
      startIndex,
      contentList,
      true,
      family === "schema" ? { schemaType, isShared } : { isShared },
    );
    dispatch(updateContentList(applyScheduleRowPatch(response, item, patch)));
  };

  const run = async (
    item: any,
    schemaType: string,
    action: ScheduleAction,
    successKey: string,
    scheduleDateTime?: string,
    patch?: ScheduleRowPatch,
  ): Promise<boolean> => {
    if (!item || Object.keys(item).length === 0) return false;
    const family = familyOf(item);
    setLoading(true);
    try {
      await contentScheduleApi.runAction({
        family,
        action,
        variables: buildVariables(item, schemaType, family, scheduleDateTime),
        isShared,
      });
      // The change itself has succeeded: a failed re-read must not report it as a failure.
      try {
        await refreshList(item, schemaType, family, patch);
      } catch (refreshError) {
        console.error("useContentSchedule: list refresh failed", refreshError);
      }
      ShowToastSuccess(t(successKey));
      return true;
    } catch (error: any) {
      const info = getScheduleErrorInfo(error);
      const statusCode = error?.networkError?.statusCode || 0;
      ShowToastError(getScheduleErrorText(error, t), statusCode);
      // The trigger is gone (it already ran, or someone cancelled it): the row is stale, refresh it.
      // Every other failure leaves the row exactly as it was.
      if (info.messageKey === "schedule_not_found") {
        try {
          await refreshList(item, schemaType, family);
        } catch {
          /* a failed refresh must not mask the original message */
        }
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  /** Move a pending schedule to `newTime` (ISO-8601 UTC). Validates first; sends nothing if invalid. */
  const reschedule = (
    item: any,
    schemaType: string,
    kind: ScheduleKind,
    newTime: string,
  ): Promise<boolean> => {
    const otherPublish = item?.scheduledPublishTriggerDateTime;
    const otherUnpublish = item?.scheduledUnPublishTriggerDateTime;
    // The ten-minute publish-to-unpublish gap must hold against the schedule that is NOT changing.
    const verdict = validateSchedule({
      isSchedulePublish: kind === "publish" ? true : isScheduledInFuture(otherPublish),
      schedulePublishDateTime: kind === "publish" ? newTime : (otherPublish ?? null),
      isScheduleUnpublish: kind === "unpublish" ? true : isScheduledInFuture(otherUnpublish),
      scheduleUnpublishDateTime: kind === "unpublish" ? newTime : (otherUnpublish ?? null),
    });
    if (!verdict.valid) {
      ShowToastError(capitalizeFirstLetter(t(verdict.messageKey)));
      return Promise.resolve(false);
    }
    return run(
      item,
      schemaType,
      kind === "publish" ? "reschedulePublish" : "rescheduleUnpublish",
      kind === "publish"
        ? "schedule_publish_reschedule_toast"
        : "schedule_unpublish_reschedule_toast",
      newTime,
      { [SCHEDULE_ROW_FIELD[kind]]: newTime },
    );
  };

  /** Remove a pending schedule. The item itself is left untouched. */
  const cancel = (item: any, schemaType: string, kind: ScheduleKind): Promise<boolean> =>
    run(
      item,
      schemaType,
      kind === "publish" ? "cancelPublish" : "cancelUnpublish",
      kind === "publish" ? "schedule_publish_cancel_toast" : "schedule_unpublish_cancel_toast",
      undefined,
      { [SCHEDULE_ROW_FIELD[kind]]: null },
    );

  return { reschedule, cancel, loading };
};

export default useContentSchedule;
